/**
 * Encrypt a git PAT before POST to Garage (transport key).
 *
 * Wire format: enc:fe:v1: + url-safe base64(iv[12] || ciphertext||tag)
 * Uses AES-GCM with REACT_APP_SEC_SCAN_TOKEN_TRANSPORT_KEY (32-byte url-safe b64).
 * Garage unwraps this, then re-encrypts at rest with SEC_SCAN_TOKEN_KEY (Fernet).
 */

import config from '../config/environment';

export const TRANSPORT_PREFIX = 'enc:fe:v1:';

function padB64(s) {
  const t = String(s || '').replace(/-/g, '+').replace(/_/g, '/');
  return t + '='.repeat((4 - (t.length % 4)) % 4);
}

function b64UrlEncode(bytes) {
  let bin = '';
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const chunk = 0x8000;
  for (let i = 0; i < arr.length; i += chunk) {
    bin += String.fromCharCode(...arr.subarray(i, i + chunk));
  }
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function b64UrlDecode(s) {
  const bin = atob(padB64(s));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

async function importTransportKey(rawKey) {
  const keyBytes = b64UrlDecode(rawKey);
  if (![16, 24, 32].includes(keyBytes.length)) {
    throw new Error('REACT_APP_SEC_SCAN_TOKEN_TRANSPORT_KEY must decode to 16, 24, or 32 bytes');
  }
  return crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['encrypt']);
}

/**
 * @param {string} plaintext
 * @returns {Promise<string>} enc:fe:v1:... ciphertext for the accessToken field
 */
export async function encryptAccessTokenForTransport(plaintext) {
  const value = (plaintext || '').trim();
  if (!value) {
    throw new Error('Refusing to encrypt an empty token');
  }
  const rawKey = (config.SEC_SCAN_TOKEN_TRANSPORT_KEY || '').trim();
  if (!rawKey) {
    throw new Error(
      'REACT_APP_SEC_SCAN_TOKEN_TRANSPORT_KEY is not set; cannot encrypt the token for upload',
    );
  }
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Web Crypto is unavailable; open the app over HTTPS or localhost');
  }
  const key = await importTransportKey(rawKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(value),
  );
  const packed = new Uint8Array(iv.length + cipherBuf.byteLength);
  packed.set(iv, 0);
  packed.set(new Uint8Array(cipherBuf), iv.length);
  return `${TRANSPORT_PREFIX}${b64UrlEncode(packed)}`;
}
